// src/pages/user/UserEditPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getUsers, updateUser } from '../../api/userAPI';
import UserForm from '../../components/user/UserForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const UserEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = parseInt(id);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch all users and find the specific user
  const { data: users, isLoading, isError, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers()
  });

  const user = users?.find(u => u.user_id === userId);

  const updateMutation = useMutation({
    mutationFn: (userData) => updateUser(userId, userData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      setSnackbar({
        open: true,
        message: 'User updated successfully!',
        severity: 'success'
      });
      
      // Navigate back to user details after successful update
      setTimeout(() => {
        navigate(`/users/${userId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating user: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleSubmit = async (userData) => {
    try {
      await updateMutation.mutateAsync(userData);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading user'} />;
  }

  if (!user) {
    return <ErrorAlert message="User not found" />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/users" color="inherit">
              Users
            </Link>
            <Link component={RouterLink} to={`/users/${userId}`} color="inherit">
              {user.first_name} {user.last_name}
            </Link>
            <Typography color="textPrimary">Edit</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">Edit User</Typography>
        </div>
        
        <Button
          component={RouterLink}
          to={`/users/${userId}`}
          startIcon={<ArrowBackIcon />}
        >
          Back to User Details
        </Button>
      </Box>

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {updateMutation.error instanceof Error 
            ? updateMutation.error.message 
            : 'Error updating user'}
        </Alert>
      )}

      <UserForm 
        initialData={user}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
        isEdit={true}
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserEditPage;